import { ref } from 'vue';

const STORAGE_KEY = 'pwa_client_token';
const ASKED_KEY = 'push_permission_asked_client';

export type PushPermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export const pushPermission = ref<PushPermissionState>(
    typeof window === 'undefined' || !('Notification' in window)
        ? 'unsupported'
        : (Notification.permission as PushPermissionState),
);

function urlBase64ToUint8Array(base64: string): Uint8Array {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(b64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function saveSubscription(sub: PushSubscription): Promise<void> {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) {
        console.warn('[push] token ausente — subscription não salva');
        return;
    }

    const key = sub.getKey('p256dh');
    const auth = sub.getKey('auth');
    if (!key || !auth) {
        console.warn('[push] chaves p256dh/auth ausentes');
        return;
    }

    const res = await fetch('/api/v1/push/subscribe-client', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
            endpoint: sub.endpoint,
            public_key: btoa(String.fromCharCode(...new Uint8Array(key))),
            auth_token: btoa(String.fromCharCode(...new Uint8Array(auth))),
        }),
    });

    if (!res.ok) {
        const body = await res.text().catch(() => '');
        console.error(`[push] subscribe-client falhou HTTP ${res.status}:`, body);
        throw new Error(`push subscribe failed: ${res.status}`);
    }
}

async function subscribe(): Promise<void> {
    const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
    if (!vapidKey) {
        console.warn('[push] VITE_VAPID_PUBLIC_KEY não definida');
        return;
    }

    console.log('[push] subscribe() iniciado');
    const reg = await navigator.serviceWorker.ready;
    console.log('[push] SW pronto, scope:', reg.scope);

    let sub = await reg.pushManager.getSubscription();
    console.log('[push] subscription existente:', sub ? sub.endpoint.slice(0, 50) : 'nenhuma');

    // Se a subscription existente foi criada com outra VAPID key, recria.
    if (sub) {
        try {
            await saveSubscription(sub);
            return;
        } catch {
            console.warn('[push] subscription existente inválida, recriando…');
            await sub.unsubscribe();
            sub = null;
        }
    }

    console.log('[push] criando nova subscription…');
    try {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        console.log('[push] subscription criada:', sub.endpoint.slice(0, 50));
    } catch (err) {
        console.error('[push] pushManager.subscribe() falhou:', err);
        throw err;
    }

    await saveSubscription(sub);
}

function isSupported(): boolean {
    return typeof window !== 'undefined'
        && 'PushManager' in window
        && 'Notification' in window
        && 'serviceWorker' in navigator;
}

/** Chamado automaticamente no login — pede permissão só na primeira vez. */
export async function initClientPushNotifications(): Promise<void> {
    if (!isSupported()) return;

    if (localStorage.getItem(ASKED_KEY)) {
        if (Notification.permission === 'granted') {
            await subscribe().catch(() => {});
        }
        return;
    }

    localStorage.setItem(ASKED_KEY, '1');

    const permission = await Notification.requestPermission();
    pushPermission.value = permission as PushPermissionState;
    if (permission === 'granted') {
        await subscribe().catch(() => {});
    }
}

/** Chamado pelo botão manual no Perfil — sempre solicita/reativa. */
export async function requestClientPushPermission(): Promise<void> {
    if (!isSupported()) return;

    localStorage.setItem(ASKED_KEY, '1');

    const permission = await Notification.requestPermission();
    pushPermission.value = permission as PushPermissionState;
    if (permission === 'granted') {
        await subscribe().catch(() => {});
    }
}
